/**
 * SSE 事件信封 —— 契约 §5.2（**已冻结**）。
 *
 * 四个事件名：`token` / `data` / `agent_error` / `done`。
 *
 * ⚠ 不能用浏览器原生 `EventSource`：令牌走 `Authorization` 头，而 `EventSource` 设不了头。
 *   这条是硬限制，不是选择 —— 已写成 lint 错误。读流在 `@cione/api-client`。
 *
 * 三条冻结的保证(前端可以据此写状态收敛)：
 *   1. `agent_error` 若出现，**必在 `done` 之前**(同一队列先进先出)；
 *   2. `data` 的 JSON **一定是单行** —— SSE 按行切 `data:`，换行会被拆成多条；
 *   3. `agent_error.code` **一定是点分式**。⚠ 这条此前不成立(Python 发的是裸 `INTERNAL`)，
 *      2026-09-01 已改成 `COMMON.INTERNAL`。
 *
 * ⚠ 2026-09-01 抓真实流时发现三处**契约文档没写**的形状(fixture 见
 *   `@cione/api-client` 的 `src/test/fixtures/real-agent-stream.sse`)：
 *
 *   1. **行尾是 CRLF(`\r\n`)**，不是 `\n`。SSE 规范允许，`eventsource-parser` 也按规范切行，
 *      所以 `data` 里不会残留 `\r` —— 但自己手写解析就一定会踩到；
 *   2. **每帧带 `id:` 递增序号**。本层不用它；将来做断线重连的 `Last-Event-ID` 要靠它；
 *   3. **`done` 帧带载荷**(内容是 streamChannel)，文档写的是「无载荷」。
 *      本层刻意忽略它 —— 收尾就是收尾，多解析一个字段只会多一处将来会变的耦合。
 */
import { z } from 'zod'
import { plannerPublishedEventSchema, type PlannerPublishedEvent } from './planner-v2'
import { cardEnvelopeSchema, type CardEnvelope } from './cards'

export const SSE_EVENT_NAMES = ['token', 'data', 'agent_error', 'done'] as const
export type SseEventName = (typeof SSE_EVENT_NAMES)[number]

/**
 * `agent_error` 的载荷。
 *
 * ⚠ 事件名刻意叫 `agent_error` 而不是 `error` —— 后者是 `EventSource` 的保留名。
 *   载荷里的 `type: "error"` 是历史形状，非权威，判错一律看 `code`。
 */
export const agentErrorPayloadSchema = z.looseObject({
  type: z.literal('error').optional(),
  code: z.string().min(1),
  message: z.string().nullable().default(null),
})
export type AgentErrorPayload = z.infer<typeof agentErrorPayloadSchema>

/** 解析后的流事件。界面只认这四种，原始事件名不外泄。 */
export type AgentEvent =
  | { readonly kind: 'planner-published'; readonly publication: PlannerPublishedEvent }
  | { readonly kind: 'token'; readonly text: string }
  | { readonly kind: 'card'; readonly card: CardEnvelope }
  | { readonly kind: 'error'; readonly code: string; readonly message: string | null }
  | { readonly kind: 'done' }

/** 解析不出来的原因。调用方据此决定「跳过一条」还是「忽略」，都不该中断整条流。 */
export type SseDecodeFailure =
  | {
      readonly ok: false
      readonly reason: 'unknown-event'
      readonly event: string
      readonly raw: string
    }
  | {
      readonly ok: false
      readonly reason: 'malformed-data'
      readonly event: string
      readonly raw: string
    }

export type SseDecodeResult = { readonly ok: true; readonly event: AgentEvent } | SseDecodeFailure

/**
 * 一条原始 SSE 事件 → 结构化事件。
 *
 * ⚠ 解析失败**不抛异常**：单条坏数据不该炸掉整条流。调用方拿到 `ok: false`
 *   记一条日志跳过即可 —— 这正是契约 §6.1「不能白屏」在流这一层的对应做法。
 *
 * ⚠ 未知事件名同样**静默忽略**：后端加新事件名不需要前端发版，与卡片同理。
 */
export function decodeSseEvent(event: string, data: string): SseDecodeResult {
  switch (event) {
    case 'token':
      // 纯字符串，逐字增量。⚠ 不要 trim —— 空格与换行是内容的一部分。
      return { ok: true, event: { kind: 'token', text: data } }

    case 'done':
      return { ok: true, event: { kind: 'done' } }

    case 'data': {
      const parsed = parseJson(data)
      if (!parsed.ok) return { ok: false, reason: 'malformed-data', event, raw: data }
      if (
        typeof parsed.value === 'object' &&
        parsed.value !== null &&
        'type' in parsed.value &&
        parsed.value.type === 'planner.published'
      ) {
        const publication = plannerPublishedEventSchema.safeParse(parsed.value)
        return publication.success
          ? { ok: true, event: { kind: 'planner-published', publication: publication.data } }
          : { ok: false, reason: 'malformed-data', event, raw: data }
      }
      const card = cardEnvelopeSchema.safeParse(parsed.value)
      if (!card.success) return { ok: false, reason: 'malformed-data', event, raw: data }
      return { ok: true, event: { kind: 'card', card: card.data } }
    }

    case 'agent_error': {
      const parsed = parseJson(data)
      if (!parsed.ok) return { ok: false, reason: 'malformed-data', event, raw: data }
      const payload = agentErrorPayloadSchema.safeParse(parsed.value)
      if (!payload.success) return { ok: false, reason: 'malformed-data', event, raw: data }
      return {
        ok: true,
        event: { kind: 'error', code: payload.data.code, message: payload.data.message },
      }
    }

    default:
      return { ok: false, reason: 'unknown-event', event, raw: data }
  }
}

function parseJson(raw: string): { ok: true; value: unknown } | { ok: false } {
  try {
    return { ok: true, value: JSON.parse(raw) }
  } catch {
    return { ok: false }
  }
}
